import { deepMerge, generateRandomId, urlParam } from '@slideslive/fuse-kit/utils';

import handleRequestResponse from '@/utils/handle_request_response';

const TRANSFER_COMPLETED_TTL_MS = 10 * 1000;
const TRANSFER_FAILED_TTL_MS = 30 * 1000;

function toInt(value, fallback) {
  const n = Number.parseInt(String(value ?? ''), 10);
  return Number.isFinite(n) ? n : fallback;
}

function toFloat(value, fallback) {
  const n = Number.parseFloat(String(value ?? ''));
  return Number.isFinite(n) ? n : fallback;
}

function decimatorOptions(pairs) {
  return pairs.map(([id, label]) => ({ id, label, description: null }));
}

const MOCK_DECIMATOR_SOURCES = [
  ['SDI_IN', 'SDI in'],
  ['HDMI_IN', 'HDMI in'],
  ['DUC', 'Scaler'],
];
const MOCK_DECIMATOR_AUDIO_PAIRS = [1, 2, 3, 4]
  .flatMap((group) => [1, 2].map((pair) => [`GROUP_${group}_PAIR_${pair}`, `Group ${group} pair ${pair}`]))
  .concat([['OFF', 'Off']]);
const MOCK_DECIMATOR_TIMES = ['5s', '15s', '30s', '1m', '5m', '10m', '30m']
  .map((time) => [`T_${time}`, time])
  .concat([['NEVER', 'Never']]);

function buildMockDecimator() {
  const select = (label, group, value, options) => ({
    label,
    group,
    type: 'select',
    value,
    natebox_default: value,
    options: decimatorOptions(options),
  });
  const checkbox = (label, group, value) => ({
    label,
    group,
    type: 'checkbox',
    value,
    natebox_default: value,
    options: [],
  });
  const audioPairs = (output, group, count) =>
    Object.fromEntries(
      Array.from({ length: count }, (_, i) => [
        `${output}_Pair${i + 1}`,
        select(`${output} pair ${i + 1}`, group, MOCK_DECIMATOR_AUDIO_PAIRS[i][0], MOCK_DECIMATOR_AUDIO_PAIRS),
      ]),
    );

  return {
    connected: true,
    serial: 'CLD10908',
    sdi_input: { locked: false, format: 'Unlocked' },
    hdmi_input: { locked: true, format: 'HD 1920x1080p25' },
    has_natebox_defaults: true,
    active_scaler_source: 'HDMI_IN',
    settings: {
      SO_Source: select('SDI out source', 'output', 'DUC', MOCK_DECIMATOR_SOURCES),
      HO_Source: select('HDMI out source', 'output', 'DUC', MOCK_DECIMATOR_SOURCES),
      HO_Type: select('HDMI out type', 'output', 'HDMI_RGB_444_2ch', [
        ['DVI_RGB_444', 'DVI RGB 444'],
        ['HDMI_RGB_444_2ch', 'HDMI RGB 444 2ch'],
        ['HDMI_YCbCr_422_2ch', 'HDMI YCbCr 422 2ch'],
      ]),
      DUC_H_FLIP_EN: checkbox('Horizontal flip', 'output', false),
      DUC_V_FLIP_EN: checkbox('Vertical flip', 'output', false),
      OUT_3G_B: checkbox('3G-B output', 'output', false),
      Loop_Enable: checkbox('Loop enable', 'output', false),
      PSFINEnable: checkbox('PSF-in enable', 'output', false),
      DUC_Source: select('Scaler source', 'scaler', 'AUTO', [
        ['AUTO', 'Auto (input with a signal, SDI first)'],
        ['SDI_IN', 'SDI in'],
        ['HDMI_IN', 'HDMI in'],
      ]),
      DUC_Ref: select('Scaler reference', 'scaler', 'Free_Run', [
        ['Source', 'Source'],
        ['Free_Run', 'Free Run'],
        ['SDI_IN', 'SDI in'],
        ['HDMI_IN', 'HDMI in'],
      ]),
      DUCFormat: select('Scaler output format', 'scaler', 'HD_1920x1080p25', [
        ['HD_1920x1080p25', 'HD 1920x1080p25'],
        ['HD_1920x1080p50', 'HD 1920x1080p50'],
        ['HD_1280x720p50', 'HD 1280x720p50'],
      ]),
      DUC_HF: select('Horizontal filter', 'scaler', 'AUTO', [
        ['AUTO', 'Auto'],
        ['NONE', 'None'],
        ['LOW', 'Low'],
        ['MEDIUM', 'Medium'],
        ['HIGH', 'High'],
      ]),
      ...audioPairs('HDMI', 'hdmi_audio', 4),
      ...audioPairs('SDI', 'sdi_audio', 8),
      AutoSave: checkbox('Auto-save', 'device', true),
      LCDOffTime: select('LCD off time', 'device', 'T_30s', MOCK_DECIMATOR_TIMES),
      ReturnToStatusTime: select('Return to status', 'device', 'NEVER', MOCK_DECIMATOR_TIMES),
    },
  };
}

function buildMockState() {
  return {
    success: true,
    messages: [
      {
        level: 'info',
        text: 'Runtime state snapshot',
        code: null,
        title: null,
        alert: false,
      },
    ],
    data: {
      app: {
        server_time: '2026-03-05T05:23:31.541770+00:00',
        version: '1.8.2',
        host_url: 'http://10.210.20.2',
        dashboard_nate_monitoring_url:
          'http://10.210.20.2:8427/grafana/public-dashboards/d0abd4c9109e409bade72509563a4e8b',
        dashboard_nate_networking_url:
          'http://10.210.20.2:8427/grafana/public-dashboards/74226c6221fa4542b33d1eb7cd852c3b',
      },
      nate: {
        track_name: 'RoboCam station',
        station_name: '#20',
        status: 'running',
        version: '1.19.0',
        api_url: 'http://10.210.20.2:5003',
        ui_url: 'http://10.210.20.2:5000',
        image_tag: 'dev',
        image: {
          created_at: '2026-02-18T23:12:36.966519856Z',
          created_at_timestamp: 1771452756.96652,
          digest: 'sha256:4402c0464d7d02a14ffa17557ace8c1a7e87aaafb08c84c2e881e8d074f8c123',
          full_image: 'registry.slideslive.com/slideslive/nate/nate:dev',
          image: 'slideslive/nate/nate:dev',
          nate_build_date: 'Wed Feb 18 23:09:36 UTC 2026',
          nate_config_version: '2.6.1',
          nate_web_ui_version: 'v1.16.0',
          size_bytes: 11797463224,
          tag: 'dev',
          version: '1.19.0',
          version_tag: '1.19.0-dev',
        },
        image_ref: null,
        started_at: '2026-03-04T22:02:08.889Z',
        uptime_s: 26482.65197,
      },
      channels: {
        production: {
          stable: {
            icon: 'rocket-outline.svg',
            is_running: false,
            local: {
              created_at: '2026-02-27T21:53:52.662719886Z',
              created_at_timestamp: 1772225632.66272,
              digest: 'sha256:9380291eafb375b567e102ba5831948a07095b748d6b453c9cf6f0711bb75646',
              full_image: 'registry.slideslive.com/slideslive/nate/nate:stable',
              image: 'slideslive/nate/nate:stable',
              nate_build_date: 'Fri Feb 27 21:52:43 UTC 2026',
              nate_config_version: '2.6.1',
              nate_web_ui_version: 'v1.15.1',
              size_bytes: 11797394581,
              tag: 'stable',
              version: '1.18.4',
              version_tag: '1.18.4-stable',
            },
            remote: {
              created_at: '2026-03-05T04:10:12.123456789Z',
              created_at_timestamp: 1772837412.123456,
              digest: null,
              full_image: 'registry.slideslive.com/slideslive/nate/nate:stable',
              image: 'slideslive/nate/nate:stable',
              nate_build_date: 'Thu Mar 05 04:08:01 UTC 2026',
              nate_config_version: '2.6.1',
              nate_web_ui_version: 'v1.16.1',
              size_bytes: 6384096693,
              tag: 'stable',
              version: '1.19.2',
              version_tag: '1.19.2-stable',
            },
          },
          beta: {
            icon: 'flask-outline.svg',
            is_running: false,
            local: {
              created_at: '2026-02-24T17:08:19.48432853Z',
              created_at_timestamp: 1771949299.48433,
              digest: 'sha256:6e50e72e81ce6a6eec15b2c0482852cdc534963faaffa964f25eb97c6c5a916f',
              full_image: 'registry.slideslive.com/slideslive/nate/nate:beta',
              image: 'slideslive/nate/nate:beta',
              nate_build_date: 'Tue Feb 24 17:03:46 UTC 2026',
              nate_config_version: '2.6.1',
              nate_web_ui_version: 'v1.16.0',
              size_bytes: 11797463446,
              tag: 'beta',
              version: '1.19.1',
              version_tag: '1.19.1-beta',
            },
            remote: {
              created_at: '2026-02-24T17:08:19.48432853Z',
              created_at_timestamp: 1771949299.48433,
              digest: null,
              full_image: 'registry.slideslive.com/slideslive/nate/nate:beta',
              image: 'slideslive/nate/nate:beta',
              nate_build_date: 'Tue Feb 24 17:03:46 UTC 2026',
              nate_config_version: '2.6.1',
              nate_web_ui_version: 'v1.16.0',
              size_bytes: 0,
              tag: 'beta',
              version: '1.19.1',
              version_tag: '1.19.1-beta',
            },
          },
        },
        development: {
          dev: {
            icon: null,
            is_running: true,
            local: {
              created_at: '2026-02-18T23:12:36.966519856Z',
              created_at_timestamp: 1771452756.96652,
              digest: 'sha256:4402c0464d7d02a14ffa17557ace8c1a7e87aaafb08c84c2e881e8d074f8c123',
              full_image: 'registry.slideslive.com/slideslive/nate/nate:dev',
              image: 'slideslive/nate/nate:dev',
              nate_build_date: 'Wed Feb 18 23:09:36 UTC 2026',
              nate_config_version: '2.6.1',
              nate_web_ui_version: 'v1.16.0',
              size_bytes: 11797463224,
              tag: 'dev',
              version: '1.19.0',
              version_tag: '1.19.0-dev',
            },
            remote: {
              created_at: '2026-02-18T23:31:28.592708339Z',
              created_at_timestamp: 1771453888.592708,
              digest: null,
              full_image: 'registry-dev.slideslive.dev/slideslive/nate/nate:git-a7697493',
              image: 'slideslive/nate/nate:git-a7697493',
              nate_build_date: 'Wed Feb 18 23:30:09 UTC 2026',
              nate_config_version: '2.6.1',
              nate_web_ui_version: 'v1.16.0',
              size_bytes: 11797463225,
              tag: 'git-a7697493',
              version: '1.19.0',
              version_tag: '1.19.0-git-a7697493',
            },
          },
        },
      },
      images: {
        local: [
          {
            created_at: '2026-02-24T17:08:19.48432853Z',
            created_at_timestamp: 1771949299.48433,
            digest: 'sha256:6e50e72e81ce6a6eec15b2c0482852cdc534963faaffa964f25eb97c6c5a916f',
            full_image: 'registry.slideslive.com/slideslive/nate/nate:beta',
            image: 'slideslive/nate/nate:beta',
            nate_build_date: 'Tue Feb 24 17:03:46 UTC 2026',
            nate_config_version: '2.6.1',
            nate_web_ui_version: 'v1.16.0',
            size_bytes: 11797463446,
            tag: 'beta',
            version: '1.19.1',
            version_tag: '1.19.1-beta',
          },
          {
            created_at: '2026-02-18T23:12:36.966519856Z',
            created_at_timestamp: 1771452756.96652,
            digest: 'sha256:4402c0464d7d02a14ffa17557ace8c1a7e87aaafb08c84c2e881e8d074f8c123',
            full_image: 'registry.slideslive.com/slideslive/nate/nate:dev',
            image: 'slideslive/nate/nate:dev',
            nate_build_date: 'Wed Feb 18 23:09:36 UTC 2026',
            nate_config_version: '2.6.1',
            nate_web_ui_version: 'v1.16.0',
            size_bytes: 11797463224,
            tag: 'dev',
            version: '1.19.0',
            version_tag: '1.19.0-dev',
          },
          {
            created_at: '2026-02-27T21:53:52.662719886Z',
            created_at_timestamp: 1772225632.66272,
            digest: 'sha256:9380291eafb375b567e102ba5831948a07095b748d6b453c9cf6f0711bb75646',
            full_image: 'registry.slideslive.com/slideslive/nate/nate:stable',
            image: 'slideslive/nate/nate:stable',
            nate_build_date: 'Fri Feb 27 21:52:43 UTC 2026',
            nate_config_version: '2.6.1',
            nate_web_ui_version: 'v1.15.1',
            size_bytes: 11797394581,
            tag: 'stable',
            version: '1.18.4',
            version_tag: '1.18.4-stable',
          },
        ],
        remote: [
          {
            created_at: '2026-02-24T17:08:19.48432853Z',
            created_at_timestamp: 1771949299.48433,
            digest: null,
            full_image: 'registry.slideslive.com/slideslive/nate/nate:beta',
            image: 'slideslive/nate/nate:beta',
            nate_build_date: 'Tue Feb 24 17:03:46 UTC 2026',
            nate_config_version: '2.6.1',
            nate_web_ui_version: 'v1.16.0',
            size_bytes: 0,
            tag: 'beta',
            version: '1.19.1',
            version_tag: '1.19.1-beta',
          },
          {
            created_at: '2026-02-18T23:12:36.966519856Z',
            created_at_timestamp: 1771452756.96652,
            digest: null,
            full_image: 'registry.slideslive.com/slideslive/nate/nate:dev',
            image: 'slideslive/nate/nate:dev',
            nate_build_date: 'Wed Feb 18 23:09:36 UTC 2026',
            nate_config_version: '2.6.1',
            nate_web_ui_version: 'v1.16.0',
            size_bytes: 0,
            tag: 'dev',
            version: '1.19.0',
            version_tag: '1.19.0-dev',
          },
          {
            created_at: '2026-02-26T12:00:08.65788965Z',
            created_at_timestamp: 1772103608.65789,
            digest: null,
            full_image: 'registry.slideslive.com/slideslive/nate/nate:master',
            image: 'slideslive/nate/nate:master',
            nate_build_date: 'Thu Feb 26 11:57:49 UTC 2026',
            nate_config_version: '2.6.1',
            nate_web_ui_version: 'v1.15.1',
            size_bytes: 6384096693,
            tag: 'master',
            version: '1.18.4',
            version_tag: '1.18.4-master',
          },
          {
            created_at: '2026-02-27T21:53:52.662719886Z',
            created_at_timestamp: 1772225632.66272,
            digest: null,
            full_image: 'registry.slideslive.com/slideslive/nate/nate:stable',
            image: 'slideslive/nate/nate:stable',
            nate_build_date: 'Fri Feb 27 21:52:43 UTC 2026',
            nate_config_version: '2.6.1',
            nate_web_ui_version: 'v1.15.1',
            size_bytes: 0,
            tag: 'stable',
            version: '1.18.4',
            version_tag: '1.18.4-stable',
          },
          {
            created_at: '2026-02-18T23:31:28.592708339Z',
            created_at_timestamp: 1771453888.592708,
            digest: null,
            full_image: 'registry-dev.slideslive.dev/slideslive/nate/nate:git-a7697493',
            image: 'slideslive/nate/nate:git-a7697493',
            nate_build_date: 'Wed Feb 18 23:30:09 UTC 2026',
            nate_config_version: '2.6.1',
            nate_web_ui_version: 'v1.16.0',
            size_bytes: 11797463225,
            tag: 'git-a7697493',
            version: '1.19.0',
            version_tag: '1.19.0-git-a7697493',
          },
          {
            created_at: '2026-02-18T23:12:36.966519856Z',
            created_at_timestamp: 1771452756.966519,
            digest: null,
            full_image: 'registry-dev.slideslive.dev/slideslive/nate/nate:git-b91e2d1',
            image: 'slideslive/nate/nate:git-b91e2d1',
            nate_build_date: 'Wed Feb 18 23:09:36 UTC 2026',
            nate_config_version: '2.6.1',
            nate_web_ui_version: 'v1.16.0',
            size_bytes: 11797463226,
            tag: 'git-b91e2d1',
            version: '1.19.0',
            version_tag: '1.19.0-git-b91e2d1',
          },
        ],
        checked_at: '2026-03-05T05:16:12.703600+00:00',
      },
      restart_targets: {
        items: [
          {
            id: 'nate_software',
            label: 'Restart NATE (software only)',
            description:
              'Restarts only the running NATE software. The main camera will move to the Home view. NATE will be unavailable for approximately 30 seconds. Camera feeds and the live-stream are not affected.',
            dangerous: true,
            confirm: true,
          },
          {
            id: 'system_pc',
            label: 'Restart NATE PC',
            description:
              'Restarts the entire NATE PC, including the NATE software, NATE Runner, and the system itself. The process takes about 1 minute.',
            dangerous: true,
            confirm: true,
          },
          {
            id: 'kvm_switch',
            label: 'Restart Network Switch',
            description: 'Hardware power-cycle of the network switch. All local devices lose connectivity for ~30 s.',
            warning:
              'Also power-cycles all PoE-powered devices — this restarts the main camera! (Z CAM keeps running on its battery.)',
            dangerous: true,
            confirm: true,
          },
        ],
      },
      transfers: {},
      wan: {
        type: 'ethernet',
        provider: 'Ethernet',
        band: null,
        online: true,
        speedtest: {},
        cellular_supported: true,
        failover_supported: true,
        failover: {
          enabled: false,
          interfaces: [
            {
              type: 'ethernet',
              enabled: true,
              priority: 1,
              active: true,
              online: true,
              provider: null,
              ip: '192.168.1.54',
              mac: '94:83:c4:00:00:01',
            },
            {
              type: 'wifi',
              enabled: false,
              priority: 2,
              active: false,
              online: false,
              provider: 'SlidesLive',
              ip: null,
              password: 'slideslive-guest',
            },
            { type: 'cellular', enabled: false, priority: 3, active: false, online: false, provider: null, ip: null },
          ],
        },
      },
      port_forwarding: {
        status: 'disabled',
        wan_ip: '192.168.88.120',
        target_ip: '10.210.20.2',
        ports: [80, 5000, 5003, 5004, 8427],
      },
      decimator: buildMockDecimator(),
      wiring: {
        fields: {
          production: {
            camera_ip: {
              label: 'Main Camera IP',
              description: 'IP address of the main camera. By default 10.210.RC_NUMBER.3',
              type: 'text',
              default: '10.210.20.3',
              value: '10.210.20.3',
              options: [],
              changed: false,
            },
            nate_main_cam_input: {
              label: 'NATE Main Camera Input',
              description:
                'By default, NATE captures the main camera image and audio via an SDI cable. MJPEG is a fast alternative but does not include audio, which means Auto Panel Discussion will not work when MJPEG selected.',
              type: 'select',
              default: 'sdi_to_magewell',
              value: 'sdi_to_magewell',
              options: [
                {
                  id: 'sdi_to_magewell',
                  label: 'SDI (DEFAULT)',
                  description: 'Main camera SDI input via Magewell capture card.',
                },
                {
                  id: 'mjpeg_plus_ndi_audio',
                  label: 'MJPEG + Audio over NDI',
                  description: 'MJPEG video stream + audio captured via NDI.',
                },
              ],
              changed: false,
            },
            atem_main_cam_input: {
              label: 'ATEM Main Camera Input',
              description: 'Defines which ATEM SDI input is used for the main camera feed.',
              type: 'select',
              default: 'input_1',
              value: 'input_1',
              options: [
                {
                  id: 'input_1',
                  label: 'INPUT 1 (DEFAULT)',
                  description: 'Main camera connected directly to ATEM INPUT 1.',
                },
                {
                  id: 'input_4',
                  label: 'INPUT 4',
                  description: 'Main camera connected to ATEM INPUT 4.',
                },
              ],
              changed: false,
            },
          },
          development: {
            simulation_mode: {
              label: 'Run in simulation mode',
              description: "connect SSD with data in 'samples/wideshots'",
              type: 'checkbox',
              default: 0,
              value: 0,
              options: [],
              changed: false,
            },
            custom_volume_mounting: {
              label: 'Optional: Custom DEV volume',
              description: 'Mount custom dev volume for Run DEV',
              type: 'text',
              default: '',
              value: '',
              options: [],
              changed: false,
            },
          },
        },
      },
    },
  };
}

function isAborted(options) {
  return Boolean(options?.abortController?.signal?.aborted);
}

function makeAbortError() {
  const err = new Error('Aborted');
  err.name = 'AbortError';
  return err;
}

function normalizeSessionId(value) {
  const v = value == null ? '' : String(value).trim();
  return v ? v : null;
}

// Addresses the networks above the router give its WAN interfaces. Cellular reports none.
const MOCK_WAN_IPS = { ethernet: '192.168.1.54', wifi: '10.0.0.12' };
const MOCK_WAN_MACS = { ethernet: '94:83:c4:00:00:01', wifi: '36:6f:43:5b:2f:5c' };

export default class MockDataFetcher {
  constructor() {
    console.info('Using MockDataFetcher.');

    this._sessionId = generateRandomId(20);

    this._state = buildMockState();

    if (urlParam('mock_no_version') === 'true') {
      const prod = this._state.data.channels.production;
      Object.values(prod).forEach((ch) => {
        ch.local = null;
        ch.remote = null;
      });
      const dev = this._state.data.channels.development?.dev;
      if (dev) {
        dev.local = null;
        dev.remote = null;
      }
    }

    if (urlParam('mock_no_local') === 'true') {
      const prod = this._state.data.channels.production;
      Object.values(prod).forEach((ch) => {
        ch.local = null;
      });
      const dev = this._state.data.channels.development?.dev;
      if (dev) dev.local = null;
    }

    this._lastStateTickMs = Date.now();

    this._logs = { nate: [], runner: [] };
    this._lastLogsTickMs = Date.now();

    this._seedLogs();

    this._oneShotMessages = null;

    this._stateCalls = 0;
    this._stateFailOnceUsed = false;
  }

  _isValidSession(params) {
    const sid = normalizeSessionId(params?.session_id);
    return Boolean(sid) && sid === String(this._sessionId);
  }

  _shouldFailState() {
    const mode = String(urlParam('mock_state_fail') || '').trim();
    if (!mode) return false;

    if (mode === 'always') return true;

    if (mode === 'once') {
      if (this._stateFailOnceUsed) return false;
      this._stateFailOnceUsed = true;
      return true;
    }

    if (mode === 'every') {
      const n = toInt(urlParam('mock_state_fail_every'), 5);
      if (n <= 0) return false;
      return this._stateCalls % n === 0;
    }

    if (mode === 'rate') {
      const p = toFloat(urlParam('mock_state_fail_rate'), 0.2);
      const clamped = Math.max(0, Math.min(1, p));
      return Math.random() < clamped;
    }

    if (mode === 'after') {
      const after = toInt(urlParam('mock_state_fail_after'), 10);
      return this._stateCalls >= after;
    }

    return false;
  }

  async get(path, params = null, options = {}) {
    console.info('MockDataFetcher: GET', path, params);

    if (isAborted(options)) throw makeAbortError();
    await this._simulateDelay(options);
    if (isAborted(options)) throw makeAbortError();

    switch (path) {
      case '/open':
        return { success: true, messages: [], session_id: this._sessionId };
      case '/state':
        this._stateCalls += 1;

        if (this._shouldFailState()) {
          const err = new Error('Failed to fetch');
          err.name = 'TypeError';
          throw err;
        }

        this._applyStateTick();
        return this._getState();
      case '/logs/runner':
        return this._getLogsRunner({ params });
      case '/logs/nate':
        return this._getLogsNate({ params });
      default:
        throw new Error(`MockDataFetcher: Unhandled GET path: ${path}`);
    }
  }

  // Mirrors the real API: POST returns an action result ({success, messages, data: {}}),
  // not /state, and the fetcher shows its alert messages as notifications.
  async post(path, payload, options = {}) {
    console.info('MockDataFetcher: POST', path, payload);

    if (isAborted(options)) throw makeAbortError();
    await this._simulateDelay(options);
    if (isAborted(options)) throw makeAbortError();

    const state = this._post(path, payload);
    const messages = state?.messages || [];
    const res = { success: !messages.some((m) => m?.level === 'error'), messages, data: {} };

    handleRequestResponse(res, { method: 'POST', path, payload });

    return res;
  }

  _post(path, payload) {
    switch (path) {
      case '/nate/-/run':
        return this._postNateRun({ payload });
      case '/nate/-/stop':
        return this._postNateStop();
      case '/images/-/download':
        return this._postImagesDownload({ payload });
      case '/images/-/prune':
        return this._postImagesPrune();
      case '/images/-/delete':
        return this._postImagesDelete({ payload });
      case '/wan/-/configure':
        return this._postWanConfigure({ payload });
      case '/wan/-/speedtest':
        return this._postWanSpeedtest();
      case '/wan/-/port-forwarding':
        return this._postWanPortForwarding({ payload });
      case '/wan/-/interface':
        return this._postWanInterface({ payload });
      case '/wan/-/priorities':
        return this._postWanPriorities({ payload });
      case '/system/-/restart':
        return this._postSystemRestart({ payload });
      case '/decimator/-/settings':
        return this._postDecimatorSettings({ payload });
      case '/decimator/-/apply-natebox-defaults':
        return this._postDecimatorNateboxDefaults();
      default:
        throw new Error(`MockDataFetcher: Unhandled POST path: ${path}`);
    }
  }

  patch() {
    throw new Error('MockDataFetcher: Unhandled PATCH path');
  }

  put() {
    throw new Error('MockDataFetcher: Unhandled PUT path');
  }

  delete() {
    throw new Error('MockDataFetcher: Unhandled DELETE path');
  }

  async _simulateDelay(options = {}) {
    const delayMs = Math.random() * 250 + 250;

    return new Promise((resolve, reject) => {
      const t = setTimeout(() => resolve(), delayMs);

      const signal = options?.abortController?.signal;
      if (!signal) return;

      if (signal.aborted) {
        clearTimeout(t);
        reject(makeAbortError());
        return;
      }

      const onAbort = () => {
        clearTimeout(t);
        signal.removeEventListener('abort', onAbort);
        reject(makeAbortError());
      };

      signal.addEventListener('abort', onAbort, { once: true });
    });
  }

  _getState() {
    const out = deepMerge({}, this._state);

    if (this._oneShotMessages) {
      out.messages = this._oneShotMessages;
      this._oneShotMessages = null;
    }

    return out;
  }

  _applyStateTick() {
    const now = Date.now();
    const dtMs = Math.max(0, now - (this._lastStateTickMs || now));
    this._lastStateTickMs = now;

    const dtSec = Math.max(0, Math.floor(dtMs / 1000));
    if (!dtSec) return;

    const app = this._state?.data?.app;
    const nate = this._state?.data?.nate;
    const wan = this._state?.data?.wan;
    const transfers = this._state?.data?.transfers;
    const images = this._state?.data?.images;

    if (app?.server_time) {
      app.server_time = this._addSecondsToIsoLike(app.server_time, dtSec);
    }

    if (nate?.status === 'running') {
      nate.uptime_s = Number(nate.uptime_s || 0) + dtSec;
    }

    if (wan?.speedtest && typeof wan.speedtest.age_s === 'number') {
      wan.speedtest.age_s = wan.speedtest.age_s + dtSec;
    }

    if (images?.checked_at) {
      images.checked_at = this._addSecondsToIsoLike(images.checked_at, dtSec);
    }

    if (transfers && typeof transfers === 'object') {
      Object.values(transfers).forEach((t) => {
        if (!t) return;
        if (t.status === 'downloading' && typeof t.remaining_time_s === 'number') {
          t.remaining_time_s = Math.max(0, t.remaining_time_s - dtSec);
        }
      });

      const keepTransfersForever = urlParam('mock_keep_transfers') === 'true';
      if (!keepTransfersForever) {
        const nowMs = Date.now();

        Object.entries(transfers).forEach(([tag, t]) => {
          if (!t) return;

          if (t.status === 'completed') {
            const at = Number(t.completed_at_ms || 0);
            if (at && nowMs - at >= TRANSFER_COMPLETED_TTL_MS) {
              delete transfers[tag];
            }
          }

          if (t.status === 'failed') {
            const at = Number(t.failed_at_ms || 0);
            if (at && nowMs - at >= TRANSFER_FAILED_TTL_MS) {
              delete transfers[tag];
            }
          }
        });
      }
    }
  }

  _addSecondsToIsoLike(str, secondsToAdd) {
    const d = new Date(String(str).replace(' ', 'T'));
    if (Number.isNaN(d.getTime())) return str;

    const next = new Date(d.getTime() + Number(secondsToAdd || 0) * 1000);
    return next.toISOString();
  }

  _postNateRun({ payload }) {
    const tag = payload?.tag;
    if (!tag) throw new Error('MockDataFetcher: Missing run tag');

    const channels = this._state?.data?.channels;
    if (!channels) throw new Error('MockDataFetcher: channels missing');

    const prod = channels?.production || {};
    const dev = channels?.development?.dev || null;

    const imagesLocal = this._state?.data?.images?.local || [];
    const imagesRemote = this._state?.data?.images?.remote || [];

    const isProdTag = Boolean(prod[tag]);
    const isDevTag = typeof tag === 'string' && tag.startsWith('git-');

    if (!isProdTag && !isDevTag) {
      throw new Error(`MockDataFetcher: Unhandled run tag: ${tag}`);
    }

    Object.keys(prod).forEach((k) => {
      prod[k].is_running = isProdTag ? k === tag : false;
    });

    if (dev) dev.is_running = isDevTag;

    if (isProdTag) {
      const transfers = this._state?.data?.transfers || {};
      const tr = transfers?.[tag] || null;
      if (tr && String(tr.status || '') === 'completed') {
        delete transfers[tag];
      }
    }

    this._state.data.nate.status = 'running';
    this._state.data.nate.image_tag = tag;
    this._state.data.nate.started_at = this._state.data.app.server_time;
    this._state.data.nate.uptime_s = 0;

    if (isProdTag) {
      const img = prod[tag]?.local || prod[tag]?.remote || null;
      this._state.data.nate.version = img?.version || tag;
      this._state.data.nate.image = img ? deepMerge({}, img) : null;
    } else {
      const existingLocal = imagesLocal.find((x) => String(x?.tag || '') === String(tag || '')) || null;
      if (!existingLocal) {
        const remoteHit = imagesRemote.find((x) => String(x?.tag || '') === String(tag || '')) || null;
        if (remoteHit) {
          imagesLocal.unshift(
            deepMerge({}, remoteHit, {
              digest: remoteHit?.digest || `sha256:${generateRandomId(64)}`,
            }),
          );
        }
      }

      const img = imagesLocal.find((x) => String(x?.tag || '') === String(tag || '')) || dev?.remote || null;
      this._state.data.nate.version = img?.version || tag;
      this._state.data.nate.image = img ? deepMerge({}, img) : null;
    }

    this._oneShotMessages = [{ level: 'info', text: `Run requested: ${tag}`, code: null, title: null, alert: true }];

    return this._getState();
  }

  _postNateStop() {
    const prod = this._state?.data?.channels?.production;
    if (!prod) throw new Error('MockDataFetcher: channels.production missing');

    Object.keys(prod).forEach((k) => {
      prod[k].is_running = false;
    });

    const dev = this._state?.data?.channels?.development?.dev || null;
    if (dev) dev.is_running = false;

    this._state.data.nate.status = 'not_running';

    this._oneShotMessages = [{ level: 'info', text: 'Stop requested', code: null, title: null, alert: true }];

    return this._getState();
  }

  _postImagesDownload({ payload }) {
    const tag = payload?.tag;

    const prod = this._state?.data?.channels?.production || {};
    const dev = this._state?.data?.channels?.development?.dev || null;
    const imagesLocal = this._state?.data?.images?.local || [];
    const imagesRemote = this._state?.data?.images?.remote || [];

    if (!tag) throw new Error('MockDataFetcher: Missing download tag');

    const isProdTag = Boolean(prod[tag]);
    const isDevTag = typeof tag === 'string' && tag.startsWith('git-');

    if (!isProdTag && !isDevTag) {
      throw new Error(`MockDataFetcher: Unhandled download tag: ${tag}`);
    }

    const remote = isProdTag
      ? prod[tag]?.remote
      : imagesRemote.find((x) => String(x?.tag || '').trim() === String(tag || '').trim()) || dev?.remote;

    if (!remote) throw new Error(`MockDataFetcher: Missing remote image for ${tag}`);

    const shouldFail = Boolean(payload?.fail);

    const total = Number(remote.size_bytes ?? 0);
    const startedAt = Date.now();

    this._state.data.transfers ||= {};
    this._state.data.transfers[tag] = {
      status: 'downloading',
      status_desc: null,
      total_size_bytes: total,
      downloaded_bytes: 0,
      remaining_time_s: 30,
    };

    const tickMs = 500;
    const durationMs = 6000;

    const intervalId = setInterval(() => {
      const transfer = this._state?.data?.transfers?.[tag];
      if (!transfer || transfer.status !== 'downloading') {
        clearInterval(intervalId);
        return;
      }

      const elapsed = Date.now() - startedAt;
      const progress = Math.min(1, elapsed / durationMs);

      transfer.downloaded_bytes = Math.floor(total * progress);
      transfer.remaining_time_s = Math.max(0, Math.ceil((durationMs - elapsed) / 1000));
    }, tickMs);

    setTimeout(() => {
      const transfer = this._state?.data?.transfers?.[tag];

      if (shouldFail) {
        if (transfer) {
          transfer.status = 'failed';
          transfer.status_desc = 'Mock failure: download interrupted';
          transfer.remaining_time_s = null;
          transfer.failed_at_ms = Date.now();
        }

        clearInterval(intervalId);

        this._oneShotMessages = [
          { level: 'error', text: `Download failed: ${tag}`, code: null, title: null, alert: true },
        ];

        return;
      }

      if (transfer) {
        transfer.status = 'completed';
        transfer.downloaded_bytes = total;
        transfer.remaining_time_s = null;
        transfer.completed_at_ms = Date.now();
      }

      const exists = imagesLocal.find((x) => String(x?.tag || '') === String(remote.tag || '')) || null;

      if (!exists) {
        imagesLocal.unshift(
          deepMerge({}, remote, {
            digest: remote?.digest || `sha256:${generateRandomId(64)}`,
          }),
        );
      }

      if (isProdTag) {
        const localHit =
          imagesLocal.find((x) => String(x?.tag || '') === String(remote.tag || '')) ||
          deepMerge({}, remote, { digest: remote?.digest || `sha256:${generateRandomId(64)}` });

        prod[tag].local = deepMerge({}, localHit);
      }

      if (isDevTag && dev && dev.remote && String(dev.remote.tag || '') === String(tag || '')) {
        dev.local = imagesLocal.find((x) => String(x?.tag || '') === String(tag || '')) || dev.local;
      }

      clearInterval(intervalId);

      this._oneShotMessages = [
        { level: 'info', text: `Download completed: ${tag}`, code: null, title: null, alert: true },
      ];
    }, durationMs);

    return this._getState();
  }

  _postImagesPrune() {
    this._oneShotMessages = [
      {
        level: 'info',
        text: 'Images prune requested (mock - no changes applied)',
        code: null,
        title: null,
        alert: true,
      },
    ];

    return this._getState();
  }

  _postImagesDelete({ payload }) {
    this._oneShotMessages = [
      {
        level: 'info',
        text: `Image delete requested (mock - no changes applied): ${payload?.tag ?? '-'}`,
        code: null,
        title: null,
        alert: true,
      },
    ];

    return this._getState();
  }

  _postWanConfigure({ payload }) {
    const wan = this._state?.data?.wan;
    if (!wan) throw new Error('MockDataFetcher: wan missing');

    const type = payload?.type;
    if (type !== 'wifi' && type !== 'ethernet' && type !== 'cellular') {
      throw new Error(`MockDataFetcher: Unhandled wan type: ${type}`);
    }

    // Like the GL router: a WiFi network change only connects WiFi at its current priority,
    // and so does any change while more than one interface is enabled.
    const failover = wan.failover;
    if (failover?.interfaces?.length && (type === 'wifi' || failover.enabled)) {
      const target = failover.interfaces.find((x) => x.type === type);
      if (!target) return this._failoverError(`Unknown WAN interface: ${type}`);

      // The password "wrong" simulates a network that does not connect.
      if (type === 'wifi' && payload?.password === 'wrong') {
        const previous = target.provider ? `Reconnected to the previous WiFi network '${target.provider}'` : '';
        this._oneShotMessages = [
          {
            level: 'warning',
            text: `WiFi network '${payload.ssid}' failed: repeater did not connect within 60s\n${previous}`,
            code: null,
            title: null,
            alert: true,
          },
        ];
        return this._getState();
      }

      target.enabled = true;
      if (type === 'wifi' && payload?.ssid != null) {
        // A blank password keeps the saved key of the same network.
        target.password = payload.password || (payload.ssid === target.provider ? target.password : null);
        target.provider = payload.ssid;
      }
      this._ifaceInternet[type] = true;
      this._recomputeFailover();

      this._oneShotMessages = [
        {
          level: 'success',
          text: `${type} WAN set up and online, priorities unchanged`,
          code: null,
          title: null,
          alert: true,
        },
      ];

      return this._getState();
    }

    wan.type = type;

    if (type === 'wifi') {
      if (payload?.ssid != null) wan.provider = payload.ssid;
      if (payload?.band != null) wan.band = payload.band;
      wan.online = true;
    } else if (type === 'cellular') {
      wan.provider = 'rmnet_mhi0';
      wan.band = null;
      wan.online = true;
    } else {
      wan.provider = 'Ethernet';
      wan.band = null;
      wan.online = true;
    }

    // One interface enabled: the switched interface becomes the top priority, the others except
    // ethernet are disabled.
    if (failover?.interfaces?.length) {
      const target = failover.interfaces.find((x) => x.type === type);
      if (target) failover.interfaces = [target, ...failover.interfaces.filter((x) => x !== target)];
      failover.interfaces.forEach((x) => {
        x.enabled = x.type === type || x.type === 'ethernet';
      });
      this._ifaceInternet[type] = true;
      this._recomputeFailover();
    }

    this._oneShotMessages = [{ level: 'info', text: `WAN configured: ${type}`, code: null, title: null, alert: true }];

    return this._getState();
  }

  _postWanPortForwarding({ payload }) {
    const portForwarding = this._state?.data?.port_forwarding;
    if (!portForwarding) throw new Error('MockDataFetcher: port_forwarding missing');

    portForwarding.status = payload?.enabled ? 'enabled' : 'disabled';

    this._oneShotMessages = [
      { level: 'success', text: `Port forwarding ${portForwarding.status}.`, code: null, title: null, alert: true },
    ];

    return this._getState();
  }

  _postDecimatorSettings({ payload }) {
    const decimator = this._state?.data?.decimator;
    if (!decimator) throw new Error('MockDataFetcher: decimator missing');

    const changed = Object.entries(payload?.settings || {});
    changed.forEach(([name, value]) => {
      if (decimator.settings[name]) decimator.settings[name].value = value;
      if (name === 'DUC_Source' && value !== 'AUTO') decimator.active_scaler_source = value;
    });
    decimator.has_natebox_defaults = Object.values(decimator.settings).every(
      (setting) => setting.value === setting.natebox_default,
    );

    this._oneShotMessages = [
      {
        level: 'success',
        title: 'Decimator settings saved',
        text: changed.map(([name]) => decimator.settings[name]?.label ?? name).join(', '),
        code: null,
        alert: true,
      },
    ];

    return this._getState();
  }

  _failoverError(text) {
    this._oneShotMessages = [{ level: 'error', text, code: null, title: null, alert: true }];
    return this._getState();
  }

  // Whether each interface would have internet when enabled (mock-only, not in /state).
  get _ifaceInternet() {
    // No active SIM: cellular has no internet even when enabled.
    if (!this.__ifaceInternet) this.__ifaceInternet = { ethernet: true, wifi: true, cellular: false };
    return this.__ifaceInternet;
  }

  // Priorities follow list order; the active uplink is the first enabled interface with
  // internet (or the first enabled one when none has internet).
  _recomputeFailover() {
    const wan = this._state.data.wan;
    const interfaces = wan.failover.interfaces;

    interfaces.forEach((x, i) => {
      x.priority = i + 1;
      x.active = false;
      x.online = Boolean(x.enabled && this._ifaceInternet[x.type]);
      x.ip = x.enabled ? MOCK_WAN_IPS[x.type] || null : null;
      x.mac = x.enabled ? MOCK_WAN_MACS[x.type] || null : null;
    });

    // Like the router: failover is on whenever more than one interface is enabled.
    wan.failover.enabled = interfaces.filter((x) => x.enabled).length >= 2;

    const active = interfaces.find((x) => x.online) || interfaces.find((x) => x.enabled);
    if (!active) return;

    active.active = true;
    wan.type = active.type;
    wan.band = null;
    if (active.type === 'wifi') wan.provider = active.provider || '';
    else if (active.type === 'cellular') wan.provider = 'rmnet_mhi0';
    else wan.provider = 'Ethernet';
  }

  _postWanInterface({ payload }) {
    const failover = this._state?.data?.wan?.failover;
    if (!failover) throw new Error('MockDataFetcher: wan.failover missing');

    const iface = failover.interfaces.find((x) => x.type === payload?.type);
    if (!iface) return this._failoverError(`Unknown WAN interface: ${payload?.type}`);

    const enabled = Boolean(payload?.enabled);
    if (!enabled && iface.type === 'ethernet') {
      return this._failoverError('Ethernet WAN can not be disabled, change the priorities instead');
    }
    if (!enabled && iface.enabled && failover.interfaces.filter((x) => x.enabled).length <= 1) {
      return this._failoverError('Cannot disable the last enabled WAN interface.');
    }

    iface.enabled = enabled;
    this._recomputeFailover();

    this._oneShotMessages = [
      {
        level: 'info',
        text: `WAN interface ${iface.type} ${enabled ? 'enabled' : 'disabled'}`,
        code: null,
        title: null,
        alert: true,
      },
    ];

    return this._getState();
  }

  _postDecimatorNateboxDefaults() {
    if (!this._state?.data) throw new Error('MockDataFetcher: state missing');

    this._state.data.decimator = buildMockDecimator();

    this._oneShotMessages = [
      { level: 'success', title: 'Decimator defaults applied', text: '', code: null, alert: true },
    ];

    return this._getState();
  }

  _postWanPriorities({ payload }) {
    const failover = this._state?.data?.wan?.failover;
    if (!failover) throw new Error('MockDataFetcher: wan.failover missing');

    const order = Array.isArray(payload?.order) ? payload.order : [];
    const known = failover.interfaces.map((x) => x.type);
    const valid = order.length === known.length && known.every((type) => order.includes(type));
    if (!valid) return this._failoverError(`Invalid WAN priority order: ${order.join(', ')}`);

    failover.interfaces = order.map((type) => failover.interfaces.find((x) => x.type === type));
    this._recomputeFailover();

    this._oneShotMessages = [
      { level: 'info', text: `WAN priorities: ${order.join(' > ')}`, code: null, title: null, alert: true },
    ];

    return this._getState();
  }

  _postWanSpeedtest() {
    const wan = this._state?.data?.wan;
    if (!wan) throw new Error('MockDataFetcher: wan missing');

    const speedtest = wan.speedtest || {};

    const rand = (min, max) => Math.random() * (max - min) + min;
    const around = (value, ratio = 0.15) => {
      const v = Number(value) || 0;
      const next = v + v * rand(-ratio, ratio);
      return Math.max(0, next);
    };

    wan.online = true;
    wan.speedtest = {
      downlink_bps: Math.floor(around(speedtest.downlink_bps ?? 312500000, 0.2)),
      uplink_bps: Math.floor(around(speedtest.uplink_bps ?? 287434000, 0.2)),
      ping: Number(rand(0.2, 5).toFixed(3)),
      server: speedtest.server || 'Mock ISP',
      age_s: 0,
    };

    return this._getState();
  }

  _postSystemRestart({ payload }) {
    const id = payload?.id;

    const items = this._state?.data?.restart_targets?.items || [];
    const target = items.find((x) => x.id === id);

    if (!target) throw new Error(`MockDataFetcher: Unhandled restart target id: ${id}`);

    this._oneShotMessages = [
      { level: 'info', text: `Restart requested: ${target.label}`, code: null, title: null, alert: true },
    ];

    this._state.data.nate.status = 'restarting';

    setTimeout(() => {
      this._state.data.nate.status = 'running';
      this._state.data.nate.started_at = this._state.data.app.server_time;
      this._state.data.nate.uptime_s = 0;
    }, 1200);

    return this._getState();
  }

  _seedLogs() {
    const ts = new Date().toISOString();

    this._logs.runner = [
      { time_iso: ts, level: 'error', text: 'NATE Runner started' },
      { time_iso: ts, level: 'info', text: 'Loading config' },
      { time_iso: ts, level: 'warning', text: 'Ready' },
    ];

    this._logs.nate = [
      { time_iso: ts, level: 'info', text: 'NATE started' },
      { time_iso: ts, level: 'info', text: 'Initializing camera feeds' },
      { time_iso: ts, level: 'info', text: '[AUDIO_STATE] voices=[2]\n  id=851 compose=True\n  id=377 compose=False' },
      { time_iso: ts, level: 'info', text: 'Ready' },
    ];
  }

  _applyLogsTick() {
    const now = Date.now();
    const dtMs = Math.max(0, now - (this._lastLogsTickMs || now));
    this._lastLogsTickMs = now;

    const dtSec = Math.max(0, Math.floor(dtMs / 1000));
    if (!dtSec) return;

    for (let i = 0; i < dtSec; i++) {
      const ts = new Date(now - (dtSec - i - 1) * 1000).toISOString();

      const nate = this._state?.data?.nate || {};
      const status = String(nate.status || 'unknown');
      const tag = String(nate.image_tag || nate?.image?.tag || '-');
      const uptime = Number(nate.uptime_s || 0);

      const wanOnline = this._state?.data?.wan?.online;
      const wanStr = typeof wanOnline === 'boolean' ? (wanOnline ? 'online' : 'offline') : 'unknown';

      const transfers = this._state?.data?.transfers || {};
      const transferSummary =
        Object.entries(transfers)
          .slice(0, 3)
          .map(([k, v]) => `${k}:${v?.status || '-'}`)
          .join(', ') || 'none';

      this._logs.runner.push({
        time_iso: ts,
        level: 'info',
        text: `state status=${status} tag=${tag} uptime=${uptime}s wan=${wanStr} transfers=${transferSummary}`,
      });

      if (status === 'running') {
        this._logs.nate.push({ time_iso: ts, level: 'info', text: `heartbeat ok (tag=${tag})` });
      } else if (status === 'restarting') {
        this._logs.nate.push({ time_iso: ts, level: 'warning', text: 'restarting...' });
      } else if (status === 'not_running') {
        this._logs.nate.push({ time_iso: ts, level: 'error', text: 'not running' });
      } else {
        this._logs.nate.push({ time_iso: ts, level: 'info', text: `status=${status}` });
      }

      if (this._logs.runner.length > 2000) this._logs.runner.splice(0, this._logs.runner.length - 2000);
      if (this._logs.nate.length > 2000) this._logs.nate.splice(0, this._logs.nate.length - 2000);
    }
  }

  _getLogsEntries(kind, { tail = 100, reverse = false } = {}) {
    this._applyLogsTick();

    const src = kind === 'runner' ? this._logs.runner : this._logs.nate;

    const t = Number(tail);
    const count = Number.isFinite(t) ? Math.max(1, Math.min(2000, t)) : 100;

    let out = src.slice(Math.max(0, src.length - count));
    if (reverse === true || String(reverse) === 'true') out = out.slice().reverse();

    return out.map((x) => ({
      time_iso: String(x?.time_iso || ''),
      level: String(x?.level || ''),
      text: String(x?.text || ''),
    }));
  }

  _getLogsRunner({ params }) {
    if (!this._isValidSession(params)) {
      return { success: true, messages: [], data: [] };
    }

    const data = this._getLogsEntries('runner', {
      tail: params?.tail ?? 100,
      reverse: params?.reverse ?? false,
    });

    return {
      success: true,
      messages: [],
      data,
    };
  }

  _getLogsNate({ params }) {
    if (!this._isValidSession(params)) {
      return { success: true, messages: [], data: [] };
    }

    const data = this._getLogsEntries('nate', {
      tail: params?.tail ?? 100,
      reverse: params?.reverse ?? false,
    });

    return {
      success: true,
      messages: [],
      data,
    };
  }
}
